import { useCallback } from 'react'

interface OpenPostPayload {
  type: 'openPost'
  postUrl: string
  commentText: string
  platform?: 'instagram' | 'tiktok'
}

interface PingPayload {
  type: 'ping'
}

interface CheckRateLimitPayload {
  type: 'checkRateLimit'
}

type NalapodExtensionMessage = OpenPostPayload | PingPayload | CheckRateLimitPayload

interface ExtensionResponse {
  success?: boolean
  error?: string
  rateLimitInfo?: RateLimitStatus
}

interface RateLimitStatus {
  allowed: boolean
  reason?: 'TOO_FAST' | 'HOURLY_LIMIT'
  commentsInLastHour: number
  maxPerHour: number
  waitSeconds?: number
  resetInMinutes?: number
  remaining?: number
  secondsSinceLastComment?: number
  message?: string
  warning?: string
}

export const useNalapodExtension = () => {
  const extensionId =
    process.env.REACT_APP_NALAPOD_EXTENSION_ID ?? 'gnabcdaipfdjejbidofbdnhfnoacbkec'

  /**
   * Check if extension is installed
   */
  const checkInstalled = useCallback(async (): Promise<boolean> => {
    return await new Promise<boolean>((resolve) => {
      try {
        const message: NalapodExtensionMessage = { type: 'ping' }
        chrome.runtime.sendMessage(extensionId, message, (response) => {
          if (chrome.runtime.lastError) {
            resolve(false)
          } else {
            resolve(!!response)
          }
        })
      } catch {
        resolve(false)
      }
    })
  }, [extensionId])

  /**
   * Check current rate limit status
   */
  const checkRateLimit = useCallback(async (): Promise<RateLimitStatus> => {
    return await new Promise<RateLimitStatus>((resolve) => {
      try {
        const message: NalapodExtensionMessage = { type: 'checkRateLimit' }
        chrome.runtime.sendMessage(
          extensionId,
          message,
          (response: RateLimitStatus) => {
            if (chrome.runtime.lastError) {
              console.error('Rate limit check failed:', chrome.runtime.lastError)
              resolve({
                allowed: false,
                commentsInLastHour: 0,
                maxPerHour: 20,
                message: 'Extension not available'
              })
            } else {
              resolve(response)
            }
          }
        )
      } catch {
        resolve({
          allowed: false,
          commentsInLastHour: 0,
          maxPerHour: 20,
          message: 'Extension not available'
        })
      }
    })
  }, [extensionId])

  /**
   * Detect platform from a post URL
   */
  const detectPlatform = (url: string): 'instagram' | 'tiktok' | null => {
    if (url.includes('instagram.com')) return 'instagram'
    if (url.includes('tiktok.com')) return 'tiktok'
    return null
  }

  /**
   * Open a post (Instagram or TikTok) and prefill the comment.
   * Auto-detects platform from the URL.
   */
  const openReply = useCallback(
    async (postUrl: string, commentText: string): Promise<boolean> => {
      const platform = detectPlatform(postUrl)
      if (!platform) {
        alert('Unsupported post URL — only Instagram and TikTok are supported.')
        return false
      }

      if (!commentText.trim()) {
        alert('Please write a comment before replying.')
        return false
      }

      try {
        return await new Promise<boolean>((resolve) => {
          const message: NalapodExtensionMessage = {
            type: 'openPost',
            postUrl,
            commentText,
            platform,
          }
          console.log('🧭 openReply triggered with', { platform, postUrl, commentText, extensionId })

          chrome.runtime.sendMessage(extensionId, message, (response: ExtensionResponse) => {
            console.log('🛰️ Message sent → Extension response:', response, 'Error:', chrome.runtime.lastError)

            if (chrome.runtime.lastError) {
              console.error('Extension error:', chrome.runtime.lastError)
              alert('Please install or enable the Nalapod Comment Helper extension.')
              return resolve(false)
            }

            // Check for rate limit response
            if (response?.error === 'RATE_LIMIT_EXCEEDED' && response.rateLimitInfo) {
              alert(response.rateLimitInfo.message)
              return resolve(false)
            }

            if (response?.success) {
              console.log('Extension opened post successfully.')
              return resolve(true)
            }

            alert('Could not connect to the Nalapod Comment Helper extension.')
            resolve(false)
          })
        })
      } catch (err) {
        console.error('Error sending message to extension:', err)
        alert('Please install or enable the Nalapod Comment Helper extension.')
        return false
      }
    },
    [extensionId]
  )

  // Backward-compat alias
  const openInstagramReply = openReply

  return { checkInstalled, openReply, openInstagramReply, checkRateLimit }
}